import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, OnChanges, OnDestroy, SimpleChanges, ViewEncapsulation, inject, output, viewChild, input, signal, linkedSignal, computed } from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {alignLine, alignmentAt, indentBlocks, StyleProperty, ToggleFormat} from './rich-text-commands';
import {htmlToPlainText, plainTextToHtml, RichTextEditor} from './rich-text-editor';
import {isListActive, ListKind, toggleList} from './rich-text-lists';
import type {RichTextShape} from './rich-text-schema';
import {COLORS, FONT_NAMES, FONT_SIZES, FORMATS, RichTextTool} from './sb-rich-text-tools';

type Panel = 'format' | 'font' | 'fontSize' | 'textColor' | 'bgColor' | 'link' | 'image' | null;

// The button titles the editor had before (CKEditor's), so people and the browser tests find them.
const TOOL_TITLES: Partial<Record<RichTextTool, string>> = {
  undo: 'Undo', redo: 'Redo', bold: 'Bold', italic: 'Italic', underline: 'Underline', strike: 'Strikethrough',
  removeFormat: 'Remove Format', numberedList: 'Insert/Remove Numbered List', bulletedList: 'Insert/Remove Bulleted List',
  outdent: 'Decrease Indent', indent: 'Increase Indent', alignLeft: 'Align Left', alignCenter: 'Center',
  alignRight: 'Align Right', link: 'Link', unlink: 'Unlink', image: 'Image',
  textColor: 'Text Color', bgColor: 'Background Color', maximize: 'Maximize', variable: 'Variable', plainText: 'Plain text',
};

const TOOL_ICONS: Partial<Record<RichTextTool, string>> = {
  undo: 'fa fa-rotate-left', redo: 'fa fa-rotate-right', bold: 'fa fa-bold', italic: 'fa fa-italic',
  underline: 'fa fa-underline', strike: 'fa fa-strikethrough', removeFormat: 'fa fa-eraser',
  numberedList: 'fa fa-list-ol', bulletedList: 'fa fa-list-ul', outdent: 'fa fa-outdent', indent: 'fa fa-indent',
  alignLeft: 'fa fa-align-left', alignCenter: 'fa fa-align-center', alignRight: 'fa fa-align-right',
  link: 'fa fa-link', unlink: 'fa fa-link-slash', image: 'fa fa-image', textColor: 'fa fa-font sb-rich-text__icon-color',
  bgColor: 'fa fa-fill-drip', maximize: 'fa fa-maximize', variable: 'fa fa-users sb-rich-text__icon-accent', plainText: 'fa fa-paragraph',
};

const PANEL_TITLES: Record<Exclude<Panel, null>, string> = {
  format: 'Paragraph Format', font: 'Font Name', fontSize: 'Font Size', textColor: 'Text Color',
  bgColor: 'Background Color', link: 'Link', image: 'Image',
};

/**
 * The panel's rich-text editor (TipTap, see rich-text-editor), with its toolbar.
 *
 * `[html]` sets the text and `(htmlChange)` reports edits (not texts set from outside); it is also
 * a form control. `shape` is `inline` for the widget texts (no paragraphs, Enter is a line break)
 * or `blocks`. `tools` lists the toolbar (sb-rich-text-tools; toolsFromToolbarGroups turns groups
 * of item names into one). `inlineStyles` writes bold, italic and underline as span styles, as the
 * widget texts' CKEditor did.
 *
 * A text the editor cannot hold without changing it (a block in an inline text, a table, a
 * comment, see RichTextEditor.keeps) opens as HTML source, with a note, until someone chooses to
 * edit it with formatting.
 *
 * The variable, font browser and image tools ask the host (`variableRequest`, `fontBrowserRequest`,
 * `imageBrowseRequest`), which answers with insertText, setFontFamily or insertImage.
 */
@Component({
  selector: 'app-sb-rich-text',
  templateUrl: './sb-rich-text.component.html',
  styleUrls: ['./sb-rich-text.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SbRichTextComponent), multi: true}],
  exportAs: 'sbRichText',
  host: {
    '(document:mousedown)': 'onDocumentMouseDown($event)',
    '(keydown.escape)': 'onEscape()',
  },
})
export class SbRichTextComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private changes = inject(ChangeDetectorRef);

  readonly htmlInput = input('', {alias: 'html'});
  readonly html = linkedSignal(() => this.htmlInput());
  readonly shape = input<RichTextShape>('blocks');
  readonly tools = input<RichTextTool[]>(['undo', 'redo', '|', 'bold', 'italic', 'underline', '|', 'link']);
  readonly inlineStyles = input(false);
  readonly linkUnderlineToggle = input(false);
  readonly readOnly = input(false);
  /** Height of the text area in pixels; with autoGrow it is the least height. */
  readonly height = input(120);
  readonly autoGrow = input(false);
  readonly resizable = input(false);
  readonly plainTextInput = input(false, {alias: 'plainText'});
  readonly plainText = linkedSignal(() => this.plainTextInput());
  readonly multiline = input(true);
  /** The target a new link gets by default (the widget texts open links in a new tab). */
  readonly linkTarget = input<string | null>('_blank');
  readonly ariaLabel = input('');
  /** Shown in grey while the text is empty, as an input's placeholder is; never part of the text. May hold HTML. */
  readonly placeholder = input('');
  /** The image panel offers Browse Server (imageBrowseRequest). */
  readonly imageBrowse = input(false);

  readonly htmlChange = output<string>();
  readonly plainTextChange = output<boolean>();
  readonly ready = output<SbRichTextComponent>();
  readonly focused = output<void>();
  readonly blurred = output<void>();
  readonly variableRequest = output<void>();
  readonly fontBrowserRequest = output<void>();
  /** The image panel's Browse button: the host answers with setImageUrl. */
  readonly imageBrowseRequest = output<void>();

  readonly content = viewChild<ElementRef<HTMLElement>>('content');

  readonly fontSizes = FONT_SIZES;
  readonly fontNames = FONT_NAMES;
  readonly colors = COLORS;
  readonly formats = FORMATS;
  readonly toolTitle = TOOL_TITLES;
  readonly toolIcon = TOOL_ICONS;
  readonly panelTitle = PANEL_TITLES;

  editor: RichTextEditor | null = null;
  /**
   * Signals, since the editor is made in ngAfterViewInit, after the toolbar was checked: a
   * signal written there refreshes the view in the same pass, where a plain field would
   * change a checked binding (NG0100).
   */
  readonly editorReady = signal(false);
  readonly rawMode = signal(false);
  readonly placeholderShown = computed(() => !!this.placeholder() && !this.rawMode() && !this.html());
  maximized = false;
  panel: Panel = null;
  linkUrl = '';
  linkNewTab = true;
  linkText = '';
  imageUrl = '';
  fontBrowserLabel = '';

  private onChange: (html: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private disabledByForm = false;

  ngAfterViewInit(): void {
    this.editor = new RichTextEditor({
      element: this.content().nativeElement,
      shape: this.shape(),
      html: this.loadableHtml(this.html()),
      editable: this.editable,
      inlineStyles: this.inlineStyles(),
      linkUnderlineToggle: this.linkUnderlineToggle(),
      plainText: this.plainText(),
      multiline: this.multiline(),
      onLinkShortcut: () => this.openPanel('link'),
      onChange: (html) => this.edited(html),
      onState: () => this.changes.markForCheck(),
      onFocus: () => this.focused.emit(),
      onBlur: () => {
        this.onTouched();
        this.blurred.emit();
      },
    });
    const ariaLabel = this.ariaLabel();
    if (ariaLabel) {
      this.editor.editor.view.dom.setAttribute('aria-label', ariaLabel);
    }
    this.rawMode.set(!this.editor.keeps(this.loadableHtml(this.html())));
    this.editorReady.set(true);
    this.ready.emit(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.editor) {
      return;
    }
    if (changes['htmlInput'] && (this.html() || '') !== this.currentHtml()) {
      this.load(this.html());
    }
    if (changes['readOnly']) {
      this.editor.setEditable(this.editable);
    }
    if (changes['plainTextInput'] || changes['multiline']) {
      this.editor.setPlainText(this.plainText(), this.multiline());
    }
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.editor = null;
    this.editorReady.set(false);
  }

  writeValue(html: string): void {
    this.html.set(html || '');
    const htmlValue = this.html();
    if (this.editor && htmlValue !== this.currentHtml()) {
      this.load(htmlValue);
    }
  }

  registerOnChange(fn: (html: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabledByForm = disabled;
    this.editor?.setEditable(this.editable);
    this.changes.markForCheck();
  }

  get editable(): boolean {
    return !this.readOnly() && !this.disabledByForm;
  }

  /** The text as it is now (the HTML source in raw mode). */
  currentHtml(): string {
    return this.rawMode() ? this.html() : this.editor?.html() ?? this.html();
  }

  focus(): void {
    this.editor?.focus();
  }

  insertText(text: string): void {
    if (this.rawMode()) {
      this.insertIntoSource(text);
      return;
    }
    this.editor?.focus();
    this.editor?.insertText(text);
  }

  insertHtml(html: string): void {
    if (this.rawMode()) {
      this.insertIntoSource(html);
      return;
    }
    this.editor?.insertHtml(html);
  }

  insertImage(src: string): void {
    this.editor?.insertImage(src);
  }

  /** The font browser's answer: the family for the selection, and the @font-face that loads it. */
  setFontFamily(family: string, fontFace?: string | null): void {
    if (fontFace) {
      const id = `sb-font-face-${family.replace(/[^a-z0-9]/gi, '-')}`;
      if (!document.getElementById(id)) {
        const style = document.createElement('style');
        style.id = id;
        style.textContent = fontFace;
        document.head.appendChild(style);
      }
    }
    this.editor?.focus();
    this.editor?.setStyle('font-family', family);
    this.fontBrowserLabel = family.replace(/['"]/g, '').split(',')[0].trim();
  }

  /** The image panel's Browse answer. */
  setImageUrl(url: string): void {
    this.imageUrl = url;
    this.changes.markForCheck();
  }

  // Toolbar state

  isOn(tool: RichTextTool): boolean {
    if (!this.editor) {
      return false;
    }
    switch (tool) {
      case 'bold': case 'italic': case 'underline': case 'strike':
        return this.editor.isActive(tool as ToggleFormat);
      case 'numberedList':
        return isListActive(this.editor.editor.state, 'orderedList');
      case 'bulletedList':
        return isListActive(this.editor.editor.state, 'bulletList');
      case 'link':
        return !!this.editor.link();
      case 'alignLeft': case 'alignCenter': case 'alignRight':
        return alignmentAt(this.editor.editor.state) === tool.slice(5).toLowerCase();
      case 'plainText':
        return this.plainText();
      case 'maximize':
        return this.maximized;
      default:
        return false;
    }
  }

  isDisabled(tool: RichTextTool): boolean {
    if (!this.editorReady() || !this.editor || !this.editable) {
      return true;
    }
    if (tool === 'plainText' || tool === 'maximize' || tool === 'undo' || tool === 'redo') {
      return tool === 'undo' ? !this.editor.canUndo() : tool === 'redo' ? !this.editor.canRedo() : false;
    }
    if (this.plainText() && tool !== 'variable') {
      return true;
    }
    return tool === 'unlink' && !this.editor.link();
  }

  get currentFormat(): string {
    const parent = this.editor?.editor.state.selection.$from.parent;
    const level = parent?.type.name === 'heading' ? parent.attrs['level'] : 0;
    return this.formats.find((format) => format.level === level)?.label || 'Normal';
  }

  get currentFontSize(): string {
    return this.editor?.styleAtSelection('font-size') || '';
  }

  get currentFont(): string {
    const family = this.editor?.styleAtSelection('font-family') || '';
    return family.replace(/['"]/g, '').split(',')[0].trim();
  }

  // Toolbar actions

  run(tool: RichTextTool): void {
    const editor = this.editor;
    if (!editor || this.isDisabled(tool)) {
      return;
    }
    this.panel = null;
    switch (tool) {
      case 'undo': editor.undo(); break;
      case 'redo': editor.redo(); break;
      case 'bold': case 'italic': case 'underline': case 'strike':
        editor.toggle(tool as ToggleFormat);
        break;
      case 'removeFormat': editor.removeFormat(); break;
      case 'numberedList': this.list('orderedList'); break;
      case 'bulletedList': this.list('bulletList'); break;
      case 'indent': indentBlocks(editor.editor, 40); break;
      case 'outdent': indentBlocks(editor.editor, -40); break;
      case 'alignLeft': case 'alignCenter': case 'alignRight':
        alignLine(editor.editor, tool.slice(5).toLowerCase() as 'left' | 'center' | 'right');
        break;
      case 'unlink': editor.unsetLink(); break;
      case 'maximize': this.maximized = !this.maximized; break;
      case 'variable': this.variableRequest.emit(); break;
      case 'fontBrowser': this.fontBrowserRequest.emit(); break;
      case 'plainText': this.togglePlainText(); break;
      case 'link': case 'image': case 'format': case 'font': case 'fontSize': case 'textColor': case 'bgColor':
        this.openPanel(tool);
        return;
    }
    editor.focus();
  }

  openPanel(panel: Panel): void {
    if (this.panel === panel) {
      this.panel = null;
      this.changes.markForCheck();
      return;
    }
    this.panel = panel;
    if (panel === 'link') {
      const link = this.editor?.link();
      this.linkUrl = link?.href || '';
      this.linkNewTab = link ? link.target === '_blank' : this.linkTarget() === '_blank';
      this.linkText = '';
    }
    if (panel === 'image') {
      this.imageUrl = '';
    }
    this.changes.markForCheck();
  }

  applyLink(): void {
    const url = this.linkUrl.trim();
    if (url) {
      this.editor?.setLink(url, this.linkNewTab ? '_blank' : null, this.linkText.trim() || undefined);
    }
    this.closePanel();
  }

  removeLink(): void {
    this.editor?.unsetLink();
    this.closePanel();
  }

  applyImage(): void {
    const url = this.imageUrl.trim();
    if (url) {
      this.editor?.insertImage(url);
    }
    this.closePanel();
  }

  setFormat(level: number): void {
    const chain = this.editor?.commands;
    if (level) {
      chain?.setNode('heading', {level}).run();
    } else {
      chain?.setNode('paragraph').run();
    }
    this.closePanel();
  }

  setStyle(property: StyleProperty, value: string | null): void {
    this.editor?.setStyle(property, value);
    this.closePanel();
  }

  closePanel(): void {
    this.panel = null;
    this.editor?.focus();
  }

  /** Raw mode: the source as typed. */
  onSourceInput(value: string): void {
    this.html.set(value);
    this.edited(value);
  }

  /** Raw mode: edit with formatting, accepting what the editor cannot keep. */
  editWithFormatting(): void {
    this.rawMode.set(false);
    this.editor?.setHtml(this.html());
    this.edited(this.editor.html());
  }

  onDocumentMouseDown(event: MouseEvent): void {
    if (this.panel && !this.host.nativeElement.contains(event.target as Node)) {
      this.panel = null;
      this.changes.markForCheck();
    }
  }

  onEscape(): void {
    if (this.panel) {
      this.closePanel();
    } else if (this.maximized) {
      this.maximized = false;
    }
  }

  private edited(html: string): void {
    this.html.set(html);
    this.onChange(html);
    this.htmlChange.emit(html);
    this.changes.markForCheck();
  }

  private load(html: string): void {
    const loadable = this.loadableHtml(html);
    this.rawMode.set(!this.editor.keeps(loadable));
    this.html.set(html || '');
    if (!this.rawMode()) {
      this.editor.setHtml(loadable);
    }
    this.changes.markForCheck();
  }

  /** Plain text with newlines, in a paragraph-based text: its lines kept as line breaks. */
  private loadableHtml(html: string): string {
    const text = html || '';
    if (this.shape() === 'blocks' && !/<[a-z!\/]/i.test(text) && /\n/.test(text)) {
      return plainTextToHtml(text.replace(/\r\n?/g, '\n').trim(), true);
    }
    return text;
  }

  private list(kind: ListKind): void {
    const {state, view} = this.editor.editor;
    toggleList(state, view.dispatch, kind);
  }

  /** The share page's plain-text switch: either way, the text becomes its plain text (see htmlToPlainText). */
  private togglePlainText(): void {
    const plainText = !this.plainText();
    const html = plainTextToHtml(htmlToPlainText(this.editor.html(), this.multiline()), this.multiline());
    this.plainText.set(plainText);
    this.editor.setPlainText(plainText, this.multiline());
    this.plainTextChange.emit(plainText);
    this.editor.setHtml(html);
    this.edited(this.editor.html());
  }

  private insertIntoSource(text: string): void {
    const textarea = this.host.nativeElement.querySelector<HTMLTextAreaElement>('.sb-rich-text__source');
    if (!textarea) {
      return;
    }
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    this.onSourceInput(`${textarea.value.slice(0, start)}${text}${textarea.value.slice(end)}`);
  }
}
